import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { bootstrapPrebuiltTools } from '@/lib/prebuilt-tools';
import { AppRouter } from './router';
import type { Session } from '@supabase/supabase-js';

const PUBLIC_PATHS = new Set(['/login', '/signup', '/diagnostic', '/diagnostic/retour']);

export const App = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [workspaceError, setWorkspaceError] = useState(false);
  const [bootstrapAttempt, setBootstrapAttempt] = useState(0);
  const initializedUserId = useRef<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;

    const applySession = async (nextSession: Session | null) => {
      if (!active) return;
      if (!nextSession) {
        initializedUserId.current = null;
        setSession(null);
        setWorkspaceError(false);
        setLoading(false);
        // A visitor on a public page (signup, online diagnostic) stays there; any other page leads to the login.
        if (!PUBLIC_PATHS.has(window.location.pathname) && !window.location.pathname.startsWith('/opposition/')) navigate('/login', { replace: true });
        return;
      }

      if (initializedUserId.current !== nextSession.user.id) {
        const { error } = await supabase.rpc('ensure_my_workspace_membership');
        if (!active) return;
        if (error) {
          console.error('Workspace initialization failed', error.message);
          setWorkspaceError(true);
          setLoading(false);
          return;
        }
        initializedUserId.current = nextSession.user.id;
        // Base tools are installed in the workspace from the first opening (idempotent).
        void bootstrapPrebuiltTools().catch((cause: unknown) => {
          console.error('Base tools installation failed', cause instanceof Error ? cause.message : cause);
        });
      }

      setSession(nextSession);
      setWorkspaceError(false);
      setLoading(false);
    };

    void supabase.auth.getSession().then(({ data, error }) => {
      if (error) {
        console.error('Session loading failed', error.message);
        if (active) {
          setWorkspaceError(true);
          setLoading(false);
        }
        return;
      }
      void applySession(data.session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      void applySession(session);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [bootstrapAttempt, navigate]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-gray-300 border-t-gray-900 rounded-full" />
      </div>
    );
  }

  if (workspaceError) {
    return (
      <main className="flex h-screen items-center justify-center bg-domino-warm px-6">
        <div className="max-w-md text-center">
          <h1 className="font-display text-xl font-semibold text-domino-ink">
            Domino n’a pas pu préparer votre espace
          </h1>
          <p className="mt-2 text-sm text-domino-inkSoft">
            Réessayez pour terminer l’ouverture de votre compte.
          </p>
          <button
            type="button"
            className="mt-5 rounded-xl bg-domino-aubergine px-4 py-2 text-sm font-semibold text-white"
            onClick={() => {
              setLoading(true);
              setWorkspaceError(false);
              setBootstrapAttempt((attempt) => attempt + 1);
            }}
          >
            Réessayer
          </button>
        </div>
      </main>
    );
  }

  return <AppRouter session={session} />;
};
